import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { Tooltip } from 'antd';

import styled from 'styled-components';

// Spazio da tenere libero per la pill "+N" quando qualche tag resta fuori.
const OVERFLOW_PILL_WIDTH = 42;

const Strip = styled.span`
    /* inline-flex e non flex: in un contenitore a blocco (es. la sezione Tags del progetto)
       'flex' allargherebbe la striscia fino al cap anche con una sola pill, rendendo cliccabile
       una fascia vuota. Come figlia di un flex, come nell'header modella, non cambia nulla. */
    display: inline-flex;
    align-items: center;
    gap: 0.3em;
    /* con $showAll le pill vanno a capo, senza vengono tagliate e riassunte nella pill "+N" */
    flex-wrap: ${(props) => (props.$showAll ? 'wrap' : 'nowrap')};
    overflow: ${(props) => (props.$showAll ? 'visible' : 'hidden')};
    max-width: ${(props) => props.$maxWidth};

    .tag-pill {
        /* niente shrink: le pill tengono la loro larghezza e quelle che non entrano
           spariscono nel conteggio, invece di comprimersi tutte fino a diventare illeggibili */
        flex: none;
        font-size: 0.85rem;
        line-height: 1.4em;
        font-weight: normal;
        text-transform: lowercase;
        background-color: ${(props) => props.theme.PrimaryColor};
        color: #fff;
        padding: 0.2em 0.75em 0.15em 0.75em;
        border-radius: 4px;
        white-space: nowrap;
        /* un nome molto lungo non deve mangiarsi tutta la striscia */
        max-width: 12rem;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    /* il segnaposto non e' un tag: resta in grigio chiaro */
    .tag-pill.no-tags {
        background-color: ${(props) => props.theme.LighterBorderColor};
        color: ${(props) => props.theme.TextColorMuted};
    }
`;

const HiddenTagsList = styled.div`
    display: flex;
    flex-direction: column;
    text-transform: lowercase;
`;

/**
 * Striscia dei tag applicati, per header e barre dense.
 * Mostra quanti tag entrano nello spazio disponibile e riassume gli altri in una pill "+N",
 * invece di passare a "{n} tags" appena si supera una soglia fissa.
 *
 * La misura si fa sempre con tutte le pill in pagina: quando cambiano i tag o la larghezza
 * della finestra si torna a renderizzarle tutte e si ricalcola quante ne stanno.
 *
 * showAll  salta del tutto la misura: le pill vanno a capo e si vedono tutte, senza "+N".
 *          Per i contesti con spazio verticale, come la sezione Tags del progetto.
 */
const TagsStrip = ({ tags, maxWidth = 'min(28rem, 40vw)', showAll = false }) => {
    const stripRef = useRef();
    // null = da misurare, numero = quante pill entrano
    const [fittingCount, setFittingCount] = useState(null);

    // dipendere dall'array farebbe rimisurare a ogni render: serve una chiave stabile
    const tagsKey = useMemo(() => tags.map((tag) => tag.ID).join('-'), [tags]);

    useEffect(() => {
        setFittingCount(null);
    }, [tagsKey, maxWidth]);

    useEffect(() => {
        if (showAll) {
            return undefined;
        }
        const remeasure = () => setFittingCount(null);
        window.addEventListener('resize', remeasure);
        return () => window.removeEventListener('resize', remeasure);
    }, [showAll]);

    // useLayoutEffect: si misura e si riduce prima che il browser dipinga, senza sfarfallio
    useLayoutEffect(() => {
        if (showAll || fittingCount !== null || !stripRef.current) {
            return;
        }
        const strip = stripRef.current;
        const pills = [...strip.querySelectorAll('.tag-pill')];
        // lo spazio disponibile e' il cap, non clientWidth: finche' il contenuto ci sta dentro
        // clientWidth vale quanto il contenuto stesso, e il confronto sarebbe con se stesso
        const cap = parseFloat(getComputedStyle(strip).maxWidth);
        const available = Number.isFinite(cap) ? cap : strip.clientWidth;
        const stripLeft = strip.getBoundingClientRect().left;

        let count = 0;
        for (let index = 0; index < pills.length; index += 1) {
            // il bordo destro rispetto alla striscia comprende gia' i gap precedenti
            const pillRight = pills[index].getBoundingClientRect().right - stripLeft;
            const isLastPill = index === pills.length - 1;
            if (pillRight + (isLastPill ? 0 : OVERFLOW_PILL_WIDTH) > available) {
                break;
            }
            count = index + 1;
        }
        // almeno una pill, altrimenti la striscia sparirebbe e con lei il punto da cliccare
        setFittingCount(Math.max(count, 1));
    }, [showAll, fittingCount, tagsKey]);

    if (!tags.length) {
        return (
            <Strip ref={stripRef} $maxWidth={maxWidth} $showAll={showAll}>
                <small className="tag-pill no-tags">no tags</small>
            </Strip>
        );
    }

    // con showAll fittingCount resta null, quindi visibleTags e' l'intero elenco e hiddenCount 0
    const visibleTags = fittingCount === null ? tags : tags.slice(0, fittingCount);
    const hiddenCount = tags.length - visibleTags.length;

    return (
        <Strip ref={stripRef} $maxWidth={maxWidth} $showAll={showAll}>
            {visibleTags.map((tag) => (
                <small key={tag.ID} className="tag-pill" title={tag.Name}>
                    {tag.Name}
                </small>
            ))}
            {hiddenCount > 0 && (
                <Tooltip
                    title={
                        <HiddenTagsList>
                            {tags.slice(visibleTags.length).map((tag) => (
                                <span key={tag.ID}>{tag.Name}</span>
                            ))}
                        </HiddenTagsList>
                    }
                >
                    <small className="tag-pill overflow-count">+{hiddenCount}</small>
                </Tooltip>
            )}
        </Strip>
    );
};

export default TagsStrip;
